import { useEffect, useState, useRef, useCallback } from "react";
import { Button, Checkbox } from "src/components";
import crossIcon from "src/assets/cross.svg";
import { searchProduct, stockistProductMap } from "src/services/invoice-api";
import { useSelector } from "react-redux";
import { RootState } from "src/context/store";
import { useToast } from "src/hooks/useToast";
import { useDispatch } from "react-redux";
import { invoiceActions } from "src/context/invoice-slice";
import { useEventStatus } from "src/hooks/useEventStatus";//M10-15730
import BrandConflictModal, { BrandConflictMatch } from "./brand-conflict-modal";//M10-15822

const removeSpecialCharacters = (text: string): string => {
  return text.replace(/[^\w\s]/gi, "");
};

const InvoiceMapping = ({ onCloseModal, mappingData }: { onCloseModal: () => void; mappingData: any }) => {
  const [search, setSearch] = useState(removeSpecialCharacters(mappingData?.textractProduct_item_description?.split(" ")[0]?.slice(0, 4) || ""));
  const [results, setResults] = useState<any[]>([]);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(false);
  // Editing an already-mapped row (edit pencil icon): pre-select the product it's currently
  // mapped to, so its checkbox shows checked instead of opening with nothing selected.
  const [selectedProduct, setSelectedProduct] = useState<any>(
    mappingData?.is_item_unknown === false && mappingData?.item_code
      ? {
          item_code: mappingData.item_code,
          item_description: mappingData.item_description,
          product_name: mappingData.item_description,
          brand: mappingData.item_brand,
        }
      : null
  );
  const { invoiceDetails,
    invoicesTextractData,
    invoiceMappingOrderedProducts,
    invoiceMappingOrderData } = useSelector((state: RootState) => state.invoice);
  const [mappingProduct, setMappingProduct] = useState<boolean>(false);
  // A reference order is the authoritative product list when one exists — the user must map
  // against the order's own items, not an arbitrary company-wide product search.
  const hasReferenceOrder = (invoiceDetails?.orderCodes?.length || 0) > 0;
  // M10-13517: defaults on for INST uploads; also defaults on when editing a row that was
  // originally mapped via the "Company products" search, so re-opening it for edit shows the
  // same search mode instead of silently falling back to the order list. Never defaults on when
  // a reference order exists, regardless of upload_type/previous mapping.
  const [companySelected, setCompanySelected] = useState<boolean>(
    !hasReferenceOrder && (invoiceDetails?.upload_type === 'INST' || !!mappingData?.is_company_selected)
  );
  const itemsPerPage = 10;
  const { toast } = useToast();
  const dispatch = useDispatch();
  const { withEventCheck } = useEventStatus();//M10-15730
  //M10-15822 — populated only when OMS blocks an "Others" mapping on a brand conflict
  const [brandConflictMatches, setBrandConflictMatches] = useState<BrandConflictMatch[]>([]);

  const pageRef = useRef(1);
  const loadingRef = useRef(false);
  const hasMoreRef = useRef(false);
  const companySelectedRef = useRef(false);
  const searchRef = useRef(search);
  const scrollRef = useRef<HTMLDivElement>(null);

  // keep refs in sync
  useEffect(() => { searchRef.current = search; }, [search]);
  useEffect(() => { companySelectedRef.current = companySelected; }, [companySelected]);

  const fetchProducts = async (currentPage: number, isFresh: boolean, currentSearch: string) => {
    if (loadingRef.current) return;
    loadingRef.current = true;
    setLoading(true);
    try {
      const res = await searchProduct({
        search: currentSearch,
        page: currentPage,
        perPage: itemsPerPage,
        company_code: invoiceDetails?.company_code,
        //M10-15818
        ...(invoiceDetails?.module_code === 4 && { module_code: invoiceDetails?.module_code }),
      });
      const newData = res?.data?.data || [];
      const totalPages = res?.data?.pagination?.totalPages || 1;
      const more = currentPage < totalPages;

      setResults((prev) => (isFresh ? newData : [...prev, ...newData]));
      hasMoreRef.current = more;
      setHasMore(more);
      pageRef.current = currentPage + 1;
    } catch (error: any) {
      console.error("Failed to fetch products:", error);
      toast({ description: error?.response?.data?.message, variant: "destructive" });
    } finally {
      loadingRef.current = false;
      setLoading(false);
    }
  };

  // search debounce
  useEffect(() => {
    const timeout = setTimeout(() => {
      pageRef.current = 1;
      setResults([]);
      hasMoreRef.current = false;
      setHasMore(false);
      if (search?.length > 0 || companySelected) {
        fetchProducts(1, true, search);
      }
    }, 600);
    return () => clearTimeout(timeout);
  }, [search]);

  // company toggle
  useEffect(() => {
    pageRef.current = 1;
    setResults([]);
    loadingRef.current = false;
    if (companySelected) {
      hasMoreRef.current = true;
      setHasMore(true);
      fetchProducts(1, true, searchRef.current);
    } else {
      hasMoreRef.current = false;
      setHasMore(false);
    }
  }, [companySelected]);

  // native scroll handler
  const handleScroll = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    if (!companySelectedRef.current) return;
    const { scrollTop, scrollHeight, clientHeight } = el;
    const isNearBottom = scrollTop + clientHeight >= scrollHeight - 150;
    if (isNearBottom && hasMoreRef.current && !loadingRef.current) {
      fetchProducts(pageRef.current, false, searchRef.current);
    }
  }, []);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    el.addEventListener("scroll", handleScroll);
    return () => el.removeEventListener("scroll", handleScroll);
  }, [handleScroll]);

  const handleProductMapping = async () => {
    setMappingProduct(true);
    if (!selectedProduct) {
      toast({ description: "Please select a product", variant: "destructive" });
      setMappingProduct(false);
      return;
    }
    // M10-14999
    const invoiceProductDescription = mappingData?.textractProduct_item_description || mappingData?.item_description || "";
    const mappedItemDescription = isOthersSelected ? `${invoiceProductDescription} - Others` : selectedProduct?.item_description || selectedProduct?.product_name;
    // M10-14999
    const payload = {
      data: {
        stockist_id: invoiceDetails?.stockist_id,
        stockist_code: invoiceDetails?.stockist_medvol_code,
        item_code: selectedProduct?.item_code,
        stockist_item_description: invoiceProductDescription,//M10-13691 M10-14999
        item_description: mappedItemDescription, //M10-14999
        ucode: invoiceDetails?.u_code,
        stockist_invoice_attachment_id: invoiceDetails?.stockist_invoice_attachment_id,
      },
      event_type: "INVOICE_STOCKIST_PRODUCT_MAP",
      primary_key: invoiceDetails?.invoice?.invoice_id,
    };
    try {
      const response = await stockistProductMap(payload, invoiceDetails?.company_code);
      // M10-15730: don't treat the mapping as done until the backend has actually consumed this
      // event — otherwise a quick re-map + Confirm Invoice can race ahead of this write and
      // auto-invoice-entry ends up reading the stale (previous) product mapping.
      await withEventCheck(
        () => {
          const updatedData = invoicesTextractData.map((item: any) =>
            // item?.invoice_product_id === mappingData?.invoice_product_id
            item?.textractProduct_item_description === mappingData?.textractProduct_item_description
              ? {
                // ...item,
                invoice_product_id: mappingData?.invoice_product_id,
                unique_key: mappingData?.invoice_product_id,
                invoice_id: invoiceDetails?.invoice?.invoice_id,
                item_description: mappedItemDescription,  //M10-14999
                item_code: selectedProduct?.item_code,
                quantity: mappingData?.quantity,
                gst: mappingData?.gst,
                cash_dis_perc: mappingData?.cash_dis_perc ?? 0,
                ptr: Number(mappingData?.ptr),
                discount_type: mappingData?.discount_type,
                free_qty: mappingData?.free_qty,
                batch_no: mappingData?.batch_no,
                inv_rate: mappingData?.inv_rate,
                inv_mrp: mappingData?.inv_mrp,
                discount_amt: mappingData?.discount_amt,
                counter_code: invoiceDetails?.counter_code,
                is_item_unknown: false,
                // M10-15596: only a product picked via the "Company products" search needs the
                // brand-match check on Confirm Invoice — order-list picks and "Others" are exempt.
                is_company_selected: companySelected && !isOthersSelected,
                // The company-product search result carries a distinct "brand" field (e.g. "Ferronemia")
                // separate from item_description's fuller pack-size text (e.g. "Ferronemia 10s pack") —
                // the brand is what Confirm Invoice checks against the raw invoice text.
                item_brand: selectedProduct?.brand,
                gross_amt: (Number(mappingData?.ptr) * Number(mappingData?.quantity))?.toFixed(2),
                id: mappingData?.id //M10-10778
                // unique_key: selectedProduct?.item_code + mappingData?.batch_no,
              }
              : item
          );

          dispatch(invoiceActions.setInvoicesTextractData(updatedData));
        },
        response?.data?.data?.eventStreamCreated?.streamId,
        invoiceDetails?.company_code
      );
      toast({ description: "Product mapped successfully", variant: "default" });
      onCloseModal();
    } catch (error: any) {
      console.error("Failed to map product:", error);
      // M10-15822: a brand conflict is a hard block, not a transient failure — show the conflicting
      // products in a modal instead of a toast, and leave THIS modal open underneath so the operator
      // has to pick a real product. Keyed off the typed error name, never off the message text.
      const errorData = error?.response?.data;
      if (errorData?.error === "OTHERS_BRAND_MATCH_BLOCKED" && errorData?.details?.matches?.length) {
        setBrandConflictMatches(errorData.details.matches);
        setMappingProduct(false);
        return;
      }
      toast({ description: error?.response?.data?.message || "Failed to map product", variant: "destructive" });
    } finally {
      setMappingProduct(false);
    }
  };

  const filteredOrderedProducts = invoiceMappingOrderedProducts?.filter((item: any) => {
    const normalizedSearch = removeSpecialCharacters(search.trim().toUpperCase());
    const productName = removeSpecialCharacters(item?.product_name?.toUpperCase() || "");
    const itemCode = removeSpecialCharacters(item?.item_code?.toUpperCase() || "");
    return productName.includes(normalizedSearch) || itemCode.includes(normalizedSearch);
  });
  //M10-13691
  const OTHERS_PRODUCT = {
    item_code: "OTHERS",
    item_description: "Others",
    product_name: "Others",
    item_division: "",
    company_productcode: "OTHERS",
  };

  const isOthersSelected = selectedProduct?.item_code === "OTHERS";
  //M10-13691

  const displayList = companySelected ? results : filteredOrderedProducts;

  return (
    <div className="flex flex-col gap-y-2 w-[500px] mx-auto bg-white p-4 border border-gray-300 shadow-lg rounded-lg">
      <div className="flex justify-between items-center">
        <p className="text-normal py-1 font-semibold text-[#1B1A1F]">Product Mapping</p>
        <img onClick={onCloseModal} src={crossIcon} alt="cross" className="w-5 h-5 font-bold invert cursor-pointer" />
      </div>

      <div className="flex items-center gap-x-2">
        <span className="text-sm font-normal text-[#666666]">{mappingData?.textractProduct_item_description}</span>
      </div>
      <div className="text-sm font-normal text-[#666666] mb-2">
        Order Number:{" "}
        <span className="text-[#8051FF]">
          {invoiceDetails?.orderCodes
            ?.map((order: any) => {
              const orderCode = order?.order_code;
              const division = order?.reference_order?.division_name;
              return `${orderCode} (${division || "-"})`;
            })
            .join(", ")}
        </span>
      </div>

      <input
        placeholder="Search"
        className="w-full px-2 py-1.5 text-gray-700 bg-[#F7f7f7] border border-gray-300 rounded-lg focus:outline-none focus:ring-0"
        onChange={(e) => setSearch(removeSpecialCharacters(e.target.value))}
        value={search}
      />
      <div className="flex justify-between align-center gap-x-4 items-center my-2">
        <div className="flex gap-x-2 items-center">
          <label className="text-sm font-normal text-[#666666]">Company products</label>
          <Checkbox checked={companySelected} onChange={() => setCompanySelected(!companySelected)} />
        </div>
      </div>

      {/* scroll container — native listener, no library */}
      <div
        ref={scrollRef}
        className="h-[calc(100vh-500px)] overflow-y-auto scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100"
      >
        <table className="min-w-full">
          <thead className="bg-gray-100 sticky top-0 z-10">
            <tr>
              <th className="w-[75%] px-4 py-2 text-sm text-left text-gray-600">Item description</th>
              <th className="w-[75%] px-4 py-2 text-sm text-left text-gray-600">Division</th>
              <th className="w-[25%] px-4 py-2 text-sm text-left text-gray-600">Item code</th>
              <th className="w-[25%] px-4 py-2 text-sm text-left text-gray-600">Action</th>
            </tr>
          </thead>
          <tbody>
            {displayList?.map((item: any, index: number) => (
              <tr
                key={`${item.company_productcode}-${index}`}
                className="text-sm"
                style={item?.active_status === "N" ? { backgroundColor: "#FEE9E8", color: "#8E1F0B" } : {}}
              >
                <td className="px-4 py-2 border-t">{item.item_description || item.product_name}</td>
                <td className="px-4 py-2 border-t">
                  {item.item_division ||
                    invoiceMappingOrderData?.orderProducts
                      ?.filter(
                        (product: any) =>
                          removeSpecialCharacters(product?.product_name?.toUpperCase()?.split(" ")?.[0]?.slice(0, 4) || "") ===
                          removeSpecialCharacters(mappingData?.textractProduct_item_description?.toUpperCase()?.split(" ")?.[0]?.slice(0, 4) || "")
                      )
                      ?.map((product: any) => product?.division_name)
                      ?.filter(Boolean)
                      ?.join(", ")}
                </td>
                <td className="px-4 py-2 border-t">{item.item_code}</td>
                <td className="px-4 py-2 border-t">
                  <Checkbox
                    checked={selectedProduct?.item_code === item.item_code}
                    onChange={() => setSelectedProduct(selectedProduct?.item_code === item.item_code ? null : item)}
                  />
                </td>
              </tr>
            ))}
            {/* M10-13691 */}
            <tr key="others-option" className="text-sm bg-gray-50">
              <td className="px-4 py-2 border-t font-medium">Others</td>
              <td className="px-4 py-2 border-t">-</td>
              <td className="px-4 py-2 border-t">OTHERS</td>
              <td className="px-4 py-2 border-t">
                <Checkbox
                  checked={isOthersSelected}
                  onChange={() => setSelectedProduct(isOthersSelected ? null : OTHERS_PRODUCT)}
                />
              </td>
            </tr>
            {/* M10-13691 */}
          </tbody>
        </table>

        {loading && (
          <div className="text-center text-sm text-gray-500 my-2">Loading...</div>
        )}
        {companySelected && !loading && !hasMore && results.length > 0 && (
          <div className="text-center text-sm text-gray-400 py-2">No more products</div>
        )}
      </div>

      <Button
        isLoading={mappingProduct}
        loaderSize="small"
        onClick={handleProductMapping}
        className="mt-2 font-normal text-white bg-[#8051FF]"
        disabled={mappingProduct}
      >
        Proceed
      </Button>

      {/* M10-15822 — renders above this modal (zIndex 50) and cannot be dismissed by clicking away */}
      <BrandConflictModal
        isOpen={brandConflictMatches.length > 0}
        onClose={() => setBrandConflictMatches([])}
        matches={brandConflictMatches}
        description={mappingData?.textractProduct_item_description || mappingData?.item_description}
      />
    </div>
  );
};

export default InvoiceMapping;
